// "Sort by part type" for the bag view: groups a bag's parts into a handful of
// part families (Bricks, Slopes, Plates, ...) that match how builders store
// their parts, and orders each family by footprint (1x1, 1x2, 1x4, ... 2x2),
// plain parts before their special variants, colours of one mould together.
//
// Families are coarse on purpose: Rebrickable has ~80 categories and a typical
// bag touches ~20 of them - too many headers for 90 parts.

export const PART_FAMILIES = [
  { key: 'bricks', label: 'Bricks', cats: [11, 5, 6, 20] },
  { key: 'slopes', label: 'Slopes & curves', cats: [3, 37] },
  { key: 'plates', label: 'Plates', cats: [14, 9, 49, 21, 1] },
  { key: 'tiles', label: 'Tiles', cats: [19, 15, 67] },
  { key: 'hinges', label: 'Hinges, bars & clips', cats: [18, 32, 39] },
  { key: 'panels', label: 'Panels, windows & doors', cats: [23, 16, 47] },
  { key: 'technic', label: 'Technic', cats: [8, 12, 17, 22, 25, 26, 40, 44, 46, 51, 52, 53, 54, 55] },
  { key: 'minifig', label: 'Minifigs & accessories', cats: [13, 27, 56, 59, 60, 61, 62, 63, 64, 65, 68, 69, 70, 71, 72, 73] },
  { key: 'other', label: 'Other', cats: [] },
];

const FAMILY_BY_CAT = new Map();
PART_FAMILIES.forEach((f, i) => f.cats.forEach(c => FAMILY_BY_CAT.set(c, i)));
const OTHER_INDEX = PART_FAMILIES.length - 1;

// Fallback for parts without a Rebrickable category (name prefix)
const NAME_FALLBACK = [
  [/^(Brick Sloped|Slope|Brick Curved)\b/i, 'slopes'],
  [/^Brick\b/i, 'bricks'],
  [/^Plate\b/i, 'plates'],
  [/^Tile\b/i, 'tiles'],
  [/^(Hinge|Bar)\b/i, 'hinges'],
  [/^Technic\b/i, 'technic'],
  [/^Minifig\b/i, 'minifig'],
];

export function partFamilyIndex(part) {
  const cat = Number(part.cat_id);
  if (FAMILY_BY_CAT.has(cat)) return FAMILY_BY_CAT.get(cat);
  const name = part.name || '';
  for (const [re, key] of NAME_FALLBACK) {
    if (re.test(name)) return PART_FAMILIES.findIndex(f => f.key === key);
  }
  return OTHER_INDEX;
}

/**
 * Footprint from a Rebrickable name: first "A x B" pair, orientation-free
 * ("Slope 45° 2 x 1" == 1 x 2). Height ("1 x 2 x 2/3") is ignored; decimals
 * ("Brick Round 1.5 x 1.5") parse as numbers. Null when the name has no size.
 */
export function parseFootprint(name) {
  const m = (name || '').match(/(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)/i);
  if (!m) return null;
  const a = parseFloat(m[1]);
  const b = parseFloat(m[2]);
  return [Math.min(a, b), Math.max(a, b)];
}

// "Plate 1 x 2", "Brick 2 x 4", "Tile 1 x 2 with Groove": the basic part of a
// size, shown before the special variants (jumper, clip, grille, ...)
const PLAIN_RE = /^(Brick|Plate|Tile)\s+\d+(?:\.\d+)?\s*x\s*\d+(?:\.\d+)?(?:\s*x\s*\d+(?:\s*\d+\/\d+|\/\d+)?)?(?:\s+with Groove)?$/i;

const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });

/**
 * Groups parts per family, each group sorted by footprint → plain first →
 * name (numeric: 1 x 2 < 1 x 10) → mould. The input order (colour, light to
 * dark from the backend) is kept as the final tiebreak, so the colours of one
 * mould stay together in the familiar order.
 * Returns [{ key, label, parts, pieces }] in family order, empty ones omitted.
 */
export function groupPartsByType(parts) {
  const decorated = (parts || []).map((part, index) => ({
    part,
    index,
    family: partFamilyIndex(part),
    size: parseFootprint(part.name),
    plain: PLAIN_RE.test(part.name || '') ? 0 : 1,
  }));

  decorated.sort((x, y) =>
    x.family - y.family
    // Parts with a size first, sizeless parts (axles, figs, ...) after by name
    || (x.size ? 0 : 1) - (y.size ? 0 : 1)
    || (x.size && y.size ? (x.size[0] - y.size[0]) || (x.size[1] - y.size[1]) : 0)
    || x.plain - y.plain
    || collator.compare(x.part.name || '', y.part.name || '')
    || collator.compare(String(x.part.part_ex_id || x.part.partId), String(y.part.part_ex_id || y.part.partId))
    || x.index - y.index
  );

  const groups = [];
  for (const d of decorated) {
    let group = groups[groups.length - 1];
    if (!group || group.familyIndex !== d.family) {
      const fam = PART_FAMILIES[d.family];
      group = { familyIndex: d.family, key: fam.key, label: fam.label, parts: [], pieces: 0 };
      groups.push(group);
    }
    group.parts.push(d.part);
    group.pieces += d.part.quantity || 0;
  }
  return groups;
}
